import { RadialButtonType } from '@data/types';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { convertToClassName } from 'utilities/helper';

interface Props extends RadialButtonType {
  id: string;
  type: string;
}

const DraggableRadialButton: React.FC<Props> = ({
  id,
  value,
  question: questionText,
  questionIndex = 0,
  answerIndex = 0,
  setSelectedAnswers = () => {},
  selectedAnswers,
  className = '',
  alternativeStyle = false,
  size = 'small',
  type = '',
}) => {
  const draggableRef = useRef<HTMLElement>(null);
  const [selected, setSelected] = useState(false);
  const answerArray = selectedAnswers ? [...selectedAnswers] : [];
  const uniqueClassNameForButtons = `draggable-${convertToClassName(questionText)}`;

  const setAnswer = (
    question: string,
    answer: string,
    questionIndex: string | number = 0,
    answerIndex: string | number = 0
  ) => {
    const newAnswer = {
      question: question,
      answer: answer,
      questionIndex: Number(questionIndex),
      answerIndex: Number(answerIndex),
    };

    let existingAnswerIndex;

    // If there is a single Drag element on a page
    if (type === 'dragSingle') {
      existingAnswerIndex = answerArray
        .filter((item) => item !== undefined)
        .findIndex((item) => {
          return Number(item.questionIndex) === Number(questionIndex);
        });

      if (existingAnswerIndex !== -1) {
        answerArray[existingAnswerIndex] = newAnswer;
      } else {
        answerArray.push(newAnswer);
      }
    } else {
      // If there are multiple Drag elements
      existingAnswerIndex = answerArray
        .filter((item) => item !== undefined)
        .findIndex((item) => {
          return item.question === question;
        });

      if (existingAnswerIndex !== -1) {
        answerArray[existingAnswerIndex] = newAnswer;
      } else {
        answerArray.push(newAnswer);
      }
    }

    setSelectedAnswers([...answerArray]);
  };

  // Get bounding box for this button
  const getBoundingBox = () => {
    if (draggableRef.current) {
      return draggableRef.current.getBoundingClientRect();
    }
    return null;
  };

  const handleDrag = () => {
    // Get bounding box of this button
    const currentBox = getBoundingBox();
    if (!currentBox) return;

    // Iterate over all other buttons and check for overlap
    document
      .querySelectorAll(`.${uniqueClassNameForButtons}`)
      .forEach((button) => {
        if (button.id !== id && button instanceof HTMLElement) {
          const otherBox = button.getBoundingClientRect();
          const isOverlapping =
            currentBox.left < otherBox.right &&
            currentBox.right > otherBox.left &&
            currentBox.top < otherBox.bottom &&
            currentBox.bottom > otherBox.top;

          if (isOverlapping) {
            // Get Data attributes
            const {
              question = '',
              answer = '',
              questionindex = 0,
              answerindex = 0,
            } = button.dataset;
            setAnswer(question, answer, questionindex, answerindex);
          }
        }
      });
  };

  useEffect(() => {
    setSelected(() => {
      // If there are multiple Drag elements
      let hasBeenAnswered = answerArray
        .filter((item) => item !== undefined)
        .findIndex((item) => {
          return item.answer === value && item.question === questionText;
        });

      // If there is a single Drag element on a page
      if (type === 'dragSingle') {
        hasBeenAnswered = answerArray
          .filter((item) => item !== undefined)
          .findIndex((item) => {
            return item.answer === value;
          });
      }

      return hasBeenAnswered !== -1;
    });
  }, [selectedAnswers]);

  return (
    <div className="relative">
      <button
        data-question={questionText}
        data-answer={value}
        data-questionindex={questionIndex}
        data-answerindex={answerIndex}
        className={classNames(
          'rounded-full relative overflow-hidden border border-grey-dark border-solid relative flex items-center justify-center',
          className,
          uniqueClassNameForButtons,
          {
            'h-[25px] w-[25px]': size === 'small',
            'h-[30px] w-[30px]': size === 'large',
          }
        )}
        id={id}
        onClick={() =>
          setAnswer(questionText, value, questionIndex, answerIndex)
        }
      >
        <span className="sr-only">{value}</span>
      </button>

      {alternativeStyle && selected && (
        <Draggable
          axis="x"
          nodeRef={draggableRef}
          defaultPosition={{ x: 0, y: 0 }}
          onDrag={handleDrag}
        >
          <span
            ref={draggableRef}
            className={classNames(
              'rounded-full bg-red absolute top-0 left-0 flex items-center justify-center z-50 pointer select-none',
              {
                'h-[35px] w-[35px] -ml-[5px] -mt-[5px]': size === 'small',
                'h-[40px] w-[40px] -mt-[5px] -ml-[5px]': size === 'large',
              }
            )}
          >
            {alternativeStyle && selected && (
              <span className="text-white text-xs">&lt; &gt;</span>
            )}
          </span>
        </Draggable>
      )}

      {alternativeStyle && selected && (
        <span
          className={classNames(
            'absolute -top-full text-white flex justify-center items-center -translate-y-[25%]',
            {
              'h-[25px] w-[25px] left-0 right-0 ': size === 'small',
              'h-[30px] w-[100vw] left-1/2 -translate-x-[50%]':
                size === 'large',
            }
          )}
        >
          <span className="block text-[10px] lg:text-xs bg-blue rounded-tl-lg rounded-br-lg px-4 select-none">
            {value}
          </span>
        </span>
      )}
    </div>
  );
};

export default DraggableRadialButton;
